/*
 * Author: Dw
 * File Created: Wednesday, 24-08-07 06:18 pm
 * Last Modified: Monday, 25-03-17 03:17 pm, by Harper Yan
 */
import { type FC, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { MotionProps } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import type { BoxProps } from '@mui/material';
import { Box, Typography } from '@mui/material';
import { Icon } from '@ucard/ds/Icon';
import Image from '@/components/Image';
import { staticImgPrefix } from '@/utils/prefix';
import GradientButton from './GradientButton';
import LanguageSwitcher from './LanguageSwitcher';
import ToggleMenuButton from './ToggleMenuButton';
import type { HeaderMenuItem } from './useHeaderConfig';
function disableScroll() {
  document.body.style.overflow = 'hidden';
  document.body.style.height = '100%';
}
function enableScroll() {
  document.body.style.overflow = '';
  document.body.style.height = '';
}
const listVariants = {
  hidden: {
    opacity: 0,
    y: -20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.03
    }
  }),
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.3
    }
  }
};
type AnimatedBoxProps = BoxProps & MotionProps;
const AnimatedBox: React.FC<AnimatedBoxProps> = ({
  children,
  ...props
}) => {
  return <Box component={motion.div} {...props} data-sentry-element="Box" data-sentry-component="AnimatedBox" data-sentry-source-file="MobileHeader.tsx">
      {children}
    </Box>;
};
const BottomBtnGroups = () => {
  const {
    t
  } = useTranslation('common');
  return <Box alignItems="center" borderTop="1px solid #4D4D4D" display="flex" flex={0} gap={2} justifyContent="center" pb={4.5} pt={2} px={2} data-sentry-element="Box" data-sentry-component="BottomBtnGroups" data-sentry-source-file="MobileHeader.tsx">
      <GradientButton fullWidth sx={{
      color: '#fff',
      fontSize: '16px',
      fontWeight: 500,
      padding: '8px 0',
      minWidth: '120px'
    }} data-sentry-element="GradientButton" data-sentry-source-file="MobileHeader.tsx">
        <Link className="white" href="/login" style={{
        width: '100%',
        height: '100%',
        padding: '4px 16px'
      }} data-sentry-element="Link" data-sentry-source-file="MobileHeader.tsx">
          {t('header.login')}
        </Link>
      </GradientButton>
      <GradientButton fill fullWidth sx={{
      color: '#fff',
      fontSize: '16px',
      fontWeight: 500,
      padding: '8px 0',
      minWidth: '120px'
    }} data-sentry-element="GradientButton" data-sentry-source-file="MobileHeader.tsx">
        <Link className="white" href="/signup" style={{
        width: '100%',
        height: '100%',
        padding: '4px 16px'
      }} data-sentry-element="Link" data-sentry-source-file="MobileHeader.tsx">
          {t('header.apply-now')}
        </Link>
      </GradientButton>
    </Box>;
};
const MobileHeader: FC<{
  configs: HeaderMenuItem[];
}> = ({
  configs
}) => {
  const [open, setOpen] = useState(false);
  const {
    t
  } = useTranslation('common');
  const router = useRouter();
  useEffect(() => {
    if (open) {
      disableScroll();
    } else {
      enableScroll();
    }
    return () => {
      enableScroll();
    }; // 在组件卸载时恢复滚动
  }, [open]);
  return <Box width={1} zIndex={1000} data-sentry-element="Box" data-sentry-component="MobileHeader" data-sentry-source-file="MobileHeader.tsx">
      <Box alignItems="center" display="flex" justifyContent="flex-start" px={2} py={1.25} width={1} data-sentry-element="Box" data-sentry-source-file="MobileHeader.tsx">
        <Image alt="logo" height={24} onClick={() => {
        router.push('/');
      }} src={staticImgPrefix('/header/logo-img.webp')} style={{
        zIndex: 1000,
        cursor: 'pointer'
      }} width={36} data-sentry-element="Image" data-sentry-source-file="MobileHeader.tsx" />
      </Box>
      <Box position="absolute" right="24px" sx={{
      top: '50%',
      transform: 'translate3d(0, -50%, 0)'
    }} zIndex={10} data-sentry-element="Box" data-sentry-source-file="MobileHeader.tsx">
        <ToggleMenuButton open={open} setOpen={setOpen} data-sentry-element="ToggleMenuButton" data-sentry-source-file="MobileHeader.tsx" />
      </Box>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="MobileHeader.tsx">
        {open && <AnimatedBox animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} initial={{
        opacity: 0
      }} sx={{
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        top: '0',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9,
        background: '#212121'
      }} transition={{
        duration: 0.3
      }}>
            <Box alignItems="center"
        // borderBottom="1px solid #4D4D4D"
        display="flex" flex="0 0 44px" justifyContent="flex-end" pr={8} sx={{
          background: '#000'
        }} width={1}>
              <LanguageSwitcher isMobile />
            </Box>
            <Box flex={1} pb={1} pt="16px">
              {configs.map((item, index) => <AnimatedBox alignItems="center" animate="visible" custom={index} display="flex" exit="exit" initial="hidden" justifyContent="space-between" key={item.title} px={2} py={1.5} variants={listVariants}>
                  <Link href={item.href} key={item.title} style={{
              textDecoration: 'none',
              color: '#fff',
              fontWeight: 500,
              cursor: 'pointer',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }} target={item.target}>
                    <Typography component="span" fontSize="16px">
                      {item.title}
                    </Typography>
                    <Icon name="ArrowForward" sx={{
                fontSize: '20px'
              }} />
                  </Link>
                </AnimatedBox>)}
            </Box>
            <BottomBtnGroups />
          </AnimatedBox>}
      </AnimatePresence>
    </Box>;
};
export default MobileHeader;